import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'
import { linkChangeRequestToNode } from '@/reducers/voiceAgents/voiceAgents.actions'

// =============================================================================
// LinkToNodeModal — the manual half of AVR-878. The server traces a spoken line
// back to its node by matching the text, which only works for lines an author
// wrote into a prompt; everything else arrives Unassigned. This is how a prompt
// engineer places one of those by hand.
//
// Only prompt-bearing nodes are offered: those are the ones with a Comments
// view to land in, and the server rejects anything else anyway.
// =============================================================================

const PROMPT_NODE_TYPES = ['greeting', 'subagent', 'prompt']

const LinkToNodeModal = ({ show, onClose, request, callId }) => {
  const dispatch = useDispatch()
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  const saving = useSelector((state) => state.voiceAgents.loading.linkChangeRequest)
  const [nodeId, setNodeId] = useState(null)

  // Reset on open so the previous comment's pick is never pre-selected onto
  // this one.
  useEffect(() => {
    if (show) {
      setNodeId(null)
    }
  }, [show])

  const options = (Array.isArray(agent?.nodes) ? agent.nodes : [])
    .filter((node) => PROMPT_NODE_TYPES.includes(node.type))
    .map((node) => ({ value: node.id, label: node.title || node.id }))

  const save = async () => {
    if (isNil(nodeId) || saving) {
      return
    }
    const result = await dispatch(linkChangeRequestToNode(request.id, nodeId, callId))
    if (result) {
      onClose()
    }
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Link comment to node"
      size="small"
      footer={
        <div className="form-actions">
          <Button onClick={onClose} data-testid="link-to-node-cancel">
            Cancel
          </Button>
          <Button
            brand
            disabled={isNil(nodeId) || saving}
            loading={saving}
            onClick={save}
            data-testid="link-to-node-submit"
          >
            Link
          </Button>
        </div>
      }
    >
      <div className="voice-agents__link-node-form" data-testid="link-to-node-form">
        <p className="voice-agents__link-node-hint">
          We could not match this line to a prompt, so the comment is unassigned. Pick the node
          whose prompt produced it and it will show up in that node&apos;s Comments view in Call
          Flow.
        </p>
        {request?.highlight?.snippet && (
          <p className="voice-agents__link-node-quote" data-testid="link-to-node-quote">
            {request.highlight.snippet}
          </p>
        )}
        {isEmpty(options) ? (
          <p className="voice-agents__link-node-hint" data-testid="link-to-node-empty">
            This agent has no prompt nodes to link to.
          </p>
        ) : (
          <div className="voice-agents__link-node-label">
            <span>Node</span>
            <Select
              inputId="link-to-node-select"
              aria-label="Node"
              options={options}
              value={nodeId}
              onChange={(option) => setNodeId(option?.value ?? null)}
              placeholder="Select a node"
            />
          </div>
        )}
      </div>
    </BasicModal>
  )
}

export default LinkToNodeModal
